import React from 'react';
import {
  Alert, Box, Button, Card, CardContent, Grid, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { EditablePageField } from '../../../components/egrid';
import { createFieldObject, type EFieldConfig } from '../../../components/form';
import { formatDate } from '../../../components/renderers';

import {
  createForecastBatch, fetchForecastBatches, fetchForecastMetrics, fetchForecastPayload,
  saveForecastMetrics, saveForecastSheets, submitPayloadToBieImport,
} from './bieForecastApi';

import {
  parseDeterminantForecastMetrics, readWorkbookFile, workbookToSheets,
} from './determinantWorkbookParser';

const money = (v:any) => Number(v || 0).toLocaleString();

type State = {
  fiscalYear:string;
  scenarioCode:string;
  filename:string;
  file:any|null;
  selectedBatch:any|null;
  batches:any[];
  metrics:any[];
  payload:any[];
  loading:boolean;
  error:string;
  success:string;
};

class BieDeterminantForecastWorkbench extends React.Component<any, State> {
  state: State = {
    fiscalYear:'2026',
    scenarioCode:'FY2026_FORECAST',
    filename:'',
    file:null,
    selectedBatch:null,
    batches:[],
    metrics:[],
    payload:[],
    loading:false,
    error:'',
    success:'',
  };

  componentDidMount(){ this.loadBatches(); }

  formconfigs: { [name: string]: EFieldConfig } = {
    fyi: { id: 'fyi', label: 'Fiscal Year', type: 'fc', req: 1, width: 120, cp: { t: 'bcs', s: 'budgetcycle' } },
    scc: { id: 'scc', label: 'Scenario Code', type: 'fc', req: 1, width: 160, cp: { t: 'bcs', s: 'biescenario' } },
  };

  makefield = (fieldconf: EFieldConfig): any => {
    const { service = 'biedeterminantforecast', record = {} } = this.props;
    const mode = 'add';
    const formatters = { dob: formatDate };
    const onChange = this.onChange.bind(this);
    const extras = {};
    return createFieldObject(service, mode, fieldconf, record, formatters, onChange, extras);
  };

  onChange = (name:'fiscalYear'|'scenarioCode') => (e:any) => {
    const raw = e?.target ? e.target.value : e;
    this.setState({[name]: String(raw || '').toUpperCase()} as any);
  };

  onFile = (e:any) => {
    const file = e.target.files?.[0] || null;
    this.setState({file, filename:file?.name || ''});
  };

  loadBatches = async () => {
    this.setState({loading:true,error:'',success:''});
    try{
      const batches = await fetchForecastBatches(this.state.fiscalYear,'');
      this.setState({batches});
    }catch(err:any){ this.setState({error:err.message || String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  importWorkbook = async () => {
    const {file,fiscalYear,scenarioCode}=this.state;
    if(!file){ this.setState({error:'Please select the determinant budget workbook.'}); return; }

    this.setState({loading:true,error:'',success:''});
    try{
      const wb = await readWorkbookFile(file);
      const sheets = workbookToSheets(wb);
      const metrics = parseDeterminantForecastMetrics(wb,fiscalYear);

      const batch = await createForecastBatch(fiscalYear,scenarioCode,file.name,{
        sheetNames: wb.SheetNames,
        importedAt: new Date().toISOString(),
      });

      await saveForecastSheets(batch.rid,sheets);
      await saveForecastMetrics(batch.rid,metrics);

      const savedMetrics = await fetchForecastMetrics(batch.rid,'');
      const payload = await fetchForecastPayload(batch.rid);

      this.setState({
        selectedBatch:batch,
        metrics:savedMetrics,
        payload:payload.sd || [],
        success:`Workbook imported. Metrics: ${metrics.length}. Payload rows: ${(payload.sd || []).length}.`,
      });
      await this.loadBatches();
    }catch(err:any){ this.setState({error:err.message || String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  selectBatch = async (batch:any) => {
    this.setState({selectedBatch:batch,loading:true,error:'',success:''});
    try{
      const [metrics,payload] = await Promise.all([
        fetchForecastMetrics(batch.rid,''),
        fetchForecastPayload(batch.rid),
      ]);
      this.setState({metrics,payload:payload.sd || []});
    }catch(err:any){ this.setState({error:err.message || String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  pushToBieImport = async () => {
    const {selectedBatch,payload,scenarioCode}=this.state;
    if(!selectedBatch){ this.setState({error:'Select or import a forecast batch first.'}); return; }
    if(payload.length===0){ this.setState({error:'No payload rows generated.'}); return; }

    this.setState({loading:true,error:'',success:''});
    try{
      await submitPayloadToBieImport('FORECAST',scenarioCode,`${selectedBatch.shc}.json`,payload);
      this.setState({success:'Forecast payload submitted to BIE import service.'});
    }catch(err:any){ this.setState({error:err.message || String(err)}); }
    finally{ this.setState({loading:false}); }
  };

  renderCards(){
    const sales = this.state.metrics.filter(m=>m.mtc==='SALE_AMOUNT').reduce((s,m)=>s+Number(m.amt||0),0);
    const purchases = this.state.metrics.filter(m=>m.mtc==='PURCHASE_AMOUNT').reduce((s,m)=>s+Number(m.amt||0),0);
    const saleVol = this.state.metrics.filter(m=>m.mtc==='SALE_VOLUME').reduce((s,m)=>s+Number(m.qty||0),0);
    const purVol = this.state.metrics.filter(m=>m.mtc==='PURCHASE_VOLUME').reduce((s,m)=>s+Number(m.qty||0),0);

    const items = [
      ['Metrics', this.state.metrics.length],
      ['Payload Rows', this.state.payload.length],
      ['Sales Amount', money(sales)],
      ['Purchase Amount', money(purchases)],
      ['Sales Volume', money(saleVol)],
      ['Purchase Volume', money(purVol)],
    ];

    return <Grid container spacing={2}>{items.map(([label,value])=>(
      <Grid size={{xs:12,sm:6,md:2}} key={label}>
        <Card><CardContent><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h6" sx={{fontWeight:700}}>{value}</Typography></CardContent></Card>
      </Grid>
    ))}</Grid>
  }

  render(){
    const {fiscalYear,scenarioCode,filename,batches,metrics,payload,error,success}=this.state;
    const { fyi: fyiconf, scc: sccconf } = this.formconfigs;
    const fyifield = this.makefield(fyiconf);
    const sccfield = this.makefield(sccconf);

    return <Box sx={{p:3}}>
      <Stack direction={{xs:'column',md:'row'}} justifyContent="space-between" spacing={2} sx={{mb:3}}>
        <Box>
          <Typography variant="h4" sx={{fontWeight:700}}>BIE Determinant Forecast Import</Typography>
          <Typography variant="body2" color="text.secondary">Import SONABHY determinant budget workbook and generate forecast-driven BIE entries.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={this.loadBatches}>Refresh</Button>
          <Button variant="contained" onClick={this.importWorkbook}>Import Workbook</Button>
          <Button variant="contained" color="success" onClick={this.pushToBieImport}>Push to BIE Import</Button>
        </Stack>
      </Stack>

      <Paper sx={{p:2,mb:3}}>
        <Grid container spacing={2}>
          <Grid size={{xs:12,md:2}}><EditablePageField label="Fiscal Year" fieldobject={fyifield} value={fiscalYear} onChange={this.onChange('fiscalYear')} fullWidth /></Grid>
          <Grid size={{xs:12,md:3}}><EditablePageField label="Scenario Code" fieldobject={sccfield} value={scenarioCode} onChange={this.onChange('scenarioCode')} fullWidth /></Grid>
          <Grid size={{xs:12,md:5}}><TextField label="Selected File" value={filename} fullWidth InputProps={{readOnly:true}} /></Grid>
          <Grid size={{xs:12,md:2}}><Button variant="outlined" component="label" fullWidth sx={{height:'100%'}}>Select File<input hidden type="file" accept=".xlsx,.xls" onChange={this.onFile}/></Button></Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{mb:3}}>{error}</Alert>}
      {success && <Alert severity="success" sx={{mb:3}}>{success}</Alert>}

      <Stack spacing={3}>
        {this.renderCards()}

        <Grid container spacing={3}>
          <Grid size={{xs:12,lg:4}}>
            <Paper sx={{p:2}}>
              <Typography variant="h6" sx={{mb:2}}>Forecast Batches</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Batch</TableCell><TableCell>Status</TableCell><TableCell>File</TableCell></TableRow></TableHead><TableBody>
                {batches.map(b=><TableRow key={b.rid} hover sx={{cursor:'pointer'}} onClick={()=>this.selectBatch(b)}><TableCell>{b.shc}</TableCell><TableCell>{b.ist}</TableCell><TableCell>{b.fnm}</TableCell></TableRow>)}
              </TableBody></Table>
            </Paper>
          </Grid>

          <Grid size={{xs:12,lg:8}}>
            <Paper sx={{p:2}}>
              <Typography variant="h6" sx={{mb:2}}>Generated BIE Payload Preview</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Period</TableCell><TableCell>Entity</TableCell><TableCell>Metric/Driver</TableCell><TableCell>Location</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead><TableBody>
                {payload.slice(0,30).map((r:any,i:number)=><TableRow key={i}><TableCell>{r.pdc}</TableCell><TableCell>{r.enc}</TableCell><TableCell>{r.drc}</TableCell><TableCell>{r.lcc}</TableCell><TableCell align="right">{money(r.qty)}</TableCell><TableCell align="right">{money(r.amt)}</TableCell></TableRow>)}
              </TableBody></Table>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{p:2}}>
          <Typography variant="h6" sx={{mb:2}}>Normalized Metrics Preview</Typography>
          <Table size="small"><TableHead><TableRow><TableCell>Period</TableCell><TableCell>Metric</TableCell><TableCell>Entity</TableCell><TableCell>Source</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Amount/Rate</TableCell></TableRow></TableHead><TableBody>
            {metrics.slice(0,40).map((m:any)=><TableRow key={m.rid}><TableCell>{m.pdc}</TableCell><TableCell>{m.mtc}</TableCell><TableCell>{m.enc}</TableCell><TableCell>{m.ssh}</TableCell><TableCell align="right">{money(m.qty)}</TableCell><TableCell align="right">{money(m.amt || m.rte)}</TableCell></TableRow>)}
          </TableBody></Table>
        </Paper>
      </Stack>
    </Box>
  }
}

export default BieDeterminantForecastWorkbench;
